import React from "react";
import { CheckCircle2, Layers, Zap, ShieldCheck } from "lucide-react";
import { useLocale } from "../../i18n/LocaleContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Solution() {
  const { t } = useLocale();
  const POINTS = [
    { icon: Layers,      title: t("landing.solution.feat1.title"), body: t("landing.solution.feat1.body") },
    { icon: Zap,         title: t("landing.solution.feat2.title"), body: t("landing.solution.feat2.body") },
    { icon: ShieldCheck, title: t("landing.solution.feat3.title"), body: t("landing.solution.feat3.body") },
  ];
  const BULLETS = [
    t("landing.solution.bullet1"),
    t("landing.solution.bullet2"),
    t("landing.solution.bullet3"),
    t("landing.solution.bullet4"),
  ];
  return (
    <section
      id="solution"
      data-testid="solution-section"
      className="bg-white py-24 md:py-32 relative"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left copy */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-4">
              <span className="inline-block h-px w-8 bg-slate-300" />
              <span>{t("landing.solution.eyebrow")}</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-[56px] font-semibold tracking-[-0.02em] mt-1 text-slate-900 leading-[1.05]">
              {t("landing.solution.headline_pre")} <span className="italic font-light text-slate-500">{t("landing.solution.headline_em")}</span> {t("landing.solution.headline_post")}
            </h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">
              {t("landing.solution.body")}
            </p>

            <div className="mt-8 space-y-4">
              {BULLETS.map((b) => (
                <div
                  key={b}
                  className="flex items-start gap-3"
                  data-testid={`solution-bullet-${b.slice(0, 12).toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <CheckCircle2 size={20} className="text-[#0072CE] mt-0.5 shrink-0" />
                  <span className="text-slate-700">{b}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right cards stack */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {POINTS.map((p, i) => (
              <div
                key={p.title}
                data-testid={`solution-feature-${i}`}
                className={`rounded-xl bg-slate-50 border border-slate-200 p-6 md:p-7 hover:border-[#0072CE] hover:shadow-md transition-all ${
                  i === 0 ? "sm:col-span-2" : ""
                }`}
              >
                <div className="h-11 w-11 rounded-lg bg-[#0072CE]/10 border border-[#0072CE]/20 flex items-center justify-center text-[#0072CE] mb-5">
                  <p.icon size={20} />
                </div>
                <h3 className="font-display text-xl font-semibold text-slate-900 tracking-tight">
                  {p.title}
                </h3>
                <p className="mt-2 text-slate-600 leading-relaxed text-sm md:text-base">
                  {p.body}
                </p>
              </div>
            ))}

            <div className="sm:col-span-2 relative rounded-xl overflow-hidden border border-slate-200">
              <img
                src={`${BACKEND_URL}/api/static/site/warehouse-hero.png`}
                alt="Modern enterprise distribution operations"
                className="w-full h-44 md:h-56 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/70 via-slate-900/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="text-white font-display text-lg font-semibold">
                  {t("landing.solution.scale.title")}
                </div>
                <div className="text-white/80 text-sm">
                  {t("landing.solution.scale.body")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
